import axios, { type AxiosInstance, type AxiosRequestConfig, type Method } from 'axios';
import type { AxiosError } from 'axios';
import Cookies from 'js-cookie';

import { AUTHENTICATION_TOKEN } from 'common/constants';
import { baseUrl } from 'api/API_App';
import { getOn401Handler } from 'api/apiConfig';

// Re-export types from modules - extend as needed
import type {
  ReplaceAccountsListResponse,
  ReplaceCreateUserData,
  ReplaceUserDetail,
  ReplaceUserRoles,
  ReplaceRoles,
  Client,
  ReplaceCheckUserExists,
  ReplaceGovAgency,
} from 'modules/account/types';
import type {
  ReplaceGeneralDetails,
  ReplaceSupplierResponse,
  ReplaceFoodSafetyCertificate,
  ReplaceFoodCertificateDetail,
  ReplaceBusinessRegistrationCertificateDetail,
  ReplaceBusinessRegistrationCertificate,
  ReplaceOtherDocument,
  ReplaceOtherDocumentDetails,
  ReplaceContractFromKios,
  ReplaceContractDetail,
} from 'modules/suppliers/types';
import type { MenuDishStatisticsResponse, AccessStatisticsResponse } from 'modules/dashboard/types';
import type { MonthlyMenuFramesResponse } from 'common/types/menuPlan';
import type { PlanningPeriod } from 'common/types/planningPeriod';
import type {
  QsChannel,
  QsClassification,
  QsGroup,
  QsProvince,
  QsSuppliersResponse,
} from 'modules/quick-search/types';
import type {
  ConfirmationCode,
  CreatedConfirmationCode,
  CreateConfirmationCodeBody,
  UpdateConfirmationCodeBody,
} from 'modules/confirmation-code/types';
import type { SampleMealsWeekResponse } from 'modules/map/components/daily-dish/sample-meal/types';

// --- API response/request types (manual, extend from modules/*) ---

export interface LoginResponse {
  token?: string;
  success: boolean;
  sessionId?: string;
  /** true nếu tài khoản phụ huynh (KH-PHOTHONG) — FE phải hiện popup nhập Mã xác nhận. */
  requiresConfirmationCode?: boolean;
}

export interface VerifyConfirmationCodeResponse {
  success: boolean;
  token?: string;
}

export interface LogoutResponse {
  success: boolean;
}

export interface RegisterUserSuccess {
  userData: ReplaceUserDetail;
}

/** Client resource items - cast to specific models (BalanceCostModel, etc.) at call site */
export type GateResource = Record<string, unknown>;

/** Kios HVS info - shared shape for getHvsInfo used by useGetHvsInfo, useGetDepartmentDetail */
export interface HvsInfoFromKios {
  operationCode?: string;
  name?: string;
  wardName?: string;
  districtName?: string;
  cityName?: string;
  city?: string;
  district?: string;
  description?: string;
  [key: string]: unknown;
}

export type UserDetail = ReplaceUserDetail;
export type UserListingResult = ReplaceAccountsListResponse;
export type CreateUserPayload = ReplaceCreateUserData;
export type RoleAssignments = ReplaceUserRoles;
export type RoleAssignmentConfig = ReplaceRoles;
export type ClientFromKios = Client;
export type GovAgencyFromKios = ReplaceGovAgency;
export type UserIdResponse = ReplaceCheckUserExists;
export type SupplierProfileDetail = ReplaceGeneralDetails;

export interface LogiReceivingRecordPhotos {
  product?: string | null;
  delivery_note?: string | null;
  qr_page_screenshot?: string | null;
  qr_label?: string | null;
  extra?: string[];
  archived?: Array<{
    field?: string | null;
    url?: string | null;
    replaced_at?: string | null;
    replaced_by?: number | null;
    replaced_by_name?: string | null;
  }>;
}

export interface LogiReceivingRecord {
  source?: string;
  id?: number;
  supplier_name?: string | null;
  supplier_code?: string | null;
  product_name?: string | null;
  notes?: string | null;
  photos?: LogiReceivingRecordPhotos | null;
  site?: { id?: number; code?: string | null; name?: string | null } | null;
  recorded_by?: { id?: number; name?: string | null } | null;
  recorded_at?: string | null;
  scanned_qr_text?: string | null;
  scanned_qr_url?: string | null;
  qr_format?: string | null;
  quantity?: string | null;
  status?: string | null;
  lock_at?: string | null;
  edited_after_lock?: boolean | null;
  slip_id?: number | null;
  slip_item_id?: number | null;
  slip_no?: string | null;
  product_code?: string | null;
  quantity_delivered?: number | null;
  uom?: string | null;
  action?: string | null;
  reason?: string | null;
  match_status?: string | null;
}

interface SearchQueries {
  limit?: number;
  offset?: number;
  searchStr?: string;
}

interface ParamsConfig {
  params?: Record<string, string | number | undefined>;
  queries?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}

function buildQueryString(queries?: Record<string, string | number | boolean | undefined>): string {
  if (!queries) return '';
  const searchParams = new URLSearchParams();
  Object.entries(queries).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

async function request<T>(
  axiosInstance: AxiosInstance,
  method: Method,
  path: string,
  config?: ParamsConfig | unknown
): Promise<T> {
  const cfg = config as ParamsConfig | undefined;
  const params = cfg?.params as Record<string, string | number> | undefined;
  const queries = cfg?.queries;
  const body =
    cfg?.body ??
    (typeof config === 'object' &&
    config !== null &&
    !('params' in config) &&
    !('queries' in config)
      ? config
      : undefined);

  const url = buildUrl(path, params) + buildQueryString(queries);
  const axiosConfig: AxiosRequestConfig = { method, url };
  if (body !== undefined && method !== 'get') {
    axiosConfig.data = body;
  }

  const res = await axiosInstance.request<T>(axiosConfig);
  return res.data;
}

let _instance: (ApiClient & { axios: AxiosInstance }) | null = null;

function createApiClient(): ApiClient & { axios: AxiosInstance } {
  const axiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 20000,
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
    },
  });
  // Token set per-request so it stays fresh after login
  axiosInstance.interceptors.request.use((config) => {
    const token = Cookies.get(AUTHENTICATION_TOKEN);
    if (token) (config.headers as Record<string, string>)['hvs-token'] = token;
    return config;
  });

  // Đường dẫn đăng xuất, khai ở một chỗ vì bộ chặn 401 phải nhận ra đúng nó (xem ngay dưới).
  const LOGOUT_PATH = '/authen-user/logout';

  // On 401, trigger logout so user is redirected to login.
  //
  // TRỪ chính request đăng xuất: bộ xử lý 401 gọi logout, nên nếu request logout bị 401 mà vẫn
  // kích hoạt bộ xử lý thì nó tự gọi lại chính mình — vòng lặp vô hạn, đo được 101 request liên
  // tiếp khi token hết hạn. Vòng lặp cần hai khiếm khuyết cùng lúc mới sống (chỗ kia ở logoutFnc,
  // đã vá), chặn ở đây là lớp thứ hai để nó không sống lại theo đường khác.
  axiosInstance.interceptors.response.use(
    (res) => res,
    (err: AxiosError) => {
      const laRequestDangXuat = err.config?.url === LOGOUT_PATH;
      if (err.response?.status === 401 && !laRequestDangXuat) {
        const on401 = getOn401Handler();
        on401?.();
      }
      return Promise.reject(err);
    }
  );

  const req =
    <T>(method: Method, path: string) =>
    (config?: ParamsConfig | unknown) =>
      request<T>(axiosInstance, method, path, config);

  const api: ApiClient = {
    // --- Auth & User (logged-in) ---
    UserController_getData: () => req<UserDetail>('get', '/authen-user/data')(),
    UserController_editData: (body) => req<UserDetail>('put', '/authen-user/data')({ body }),
    UserController_logout: (config?: unknown) =>
      req<LogoutResponse>('post', LOGOUT_PATH)(config ?? {}),
    UserController_changePassword: (body: { oldPassword: string; newPassword: string }) =>
      req<LogoutResponse>('post', '/authen-user/password/change')({ body }),

    UserController_getRoleAssignments: () =>
      req<RoleAssignments>('get', '/authen-user/role-assignment')(),

    AuthenController_loginWithPassword: (body: { handle: string; password: string }) =>
      req<LoginResponse>('post', '/authen/login')(body as ParamsConfig),
    AuthenController_verifyConfirmationCode: (body: { code: string }) =>
      req<VerifyConfirmationCodeResponse>('post', '/authen/verify-confirmation-code')({ body }),
    AuthenController_loginAsGuest: () =>
      req<{ token: string }>('post', '/authen/guest')({}),

    // --- Confirmation Code (Mã xác nhận cho phụ huynh) ---
    ConfirmationCodeController_list: (config?: {
      queries?: { targetOrgId?: string; status?: string };
    }) => request<{ data: ConfirmationCode[] }>(axiosInstance, 'get', '/confirmation-codes', config),
    ConfirmationCodeController_create: (body: CreateConfirmationCodeBody) =>
      req<{ data: CreatedConfirmationCode }>('post', '/confirmation-codes')({ body }),
    ConfirmationCodeController_newCode: () =>
      req<{ data: { code: string } }>('get', '/confirmation-codes/new-code')(),
    ConfirmationCodeController_update: (config: {
      params: { id: string };
      body: UpdateConfirmationCodeBody;
    }) => req<{ data: ConfirmationCode }>('patch', '/confirmation-codes/:id')(config),

    // --- User Management ---
    UserManagementController_createUser: (body: CreateUserPayload) =>
      req<RegisterUserSuccess>('post', '/management/user')({ body }),
    UserManagementController_listUser: (config?) =>
      request<UserListingResult>(axiosInstance, 'get', '/management/user', config),
    UserManagementController_getUserData: (config: { params: { userId: string } }) =>
      req<UserDetail>('get', '/management/user/:userId')(config),
    UserManagementController_editUserData: (config: {
      params: { userId: string };
      body: unknown;
      queries?: { activate?: boolean };
    }) =>
      req<UserDetail>(
        'put',
        '/management/user/:userId'
      )({
        ...config,
        params: config.params,
      }),
    UserManagementController_changePassword: (config: {
      params: { userId: string };
      body: { newPassword: string };
    }) => req<LogoutResponse>('patch', '/management/user/:userId/change-password')(config),
    UserManagementController_editUserRoles: (config: {
      params: { userId: string };
      body: unknown;
    }) => req<LogoutResponse>('put', '/management/user/:userId/role')(config),
    /** Roles của tài khoản đang được chỉnh sửa. GET /management/user/:userId/role */
    UserManagementController_getUserRoles: (config: { params: { userId: string } }) =>
      req<RoleAssignments>('get', '/management/user/:userId/role')(config),
    UserManagementController_getUserId: (config: { params: { handle: string } }) =>
      req<UserIdResponse>('get', '/management/user/handle/:handle')(config),
    UserManagementController_getRoleAssignmentConfig: () =>
      req<RoleAssignmentConfig>('get', '/management/user/role-assignment-config')(),

    // --- Kios ---
    KiosController_listClients: (config?: { queries?: { type?: 1 | 2 } }) =>
      req<ClientFromKios[]>('get', '/external/kios/client')(config),
    KiosController_getClientDetails: (config: { params: { code: string } }) =>
      req<ClientFromKios>('get', '/external/kios/client/:code')(config),
    KiosController_listGovAgencies: () =>
      req<GovAgencyFromKios[]>('get', '/external/kios/gov-agency')(),
    KiosController_getGovAgencyDetails: (config: { params: { code: string } }) =>
      req<GovAgencyFromKios>('get', '/external/kios/gov-agency/:code')(config),
    KiosController_getHvsInfo: (config?: unknown) =>
      req<HvsInfoFromKios>('get', '/external/kios/hvs')(config ?? {}),

    // --- Quick Search (Kios external, tra cứu toàn hệ thống) ---
    QuickSearchController_getChannels: () =>
      req<QsChannel[]>('get', '/external/kios/quick-search/channels')(),
    QuickSearchController_getClassifications: (config: { queries: { channelId: number } }) =>
      req<QsClassification[]>('get', '/external/kios/quick-search/classifications')(config),
    QuickSearchController_getGroups: (config: {
      queries: { channelId: number; classificationId: number; periodId?: number };
    }) => req<QsGroup[]>('get', '/external/kios/quick-search/groups')(config),
    QuickSearchController_getSuppliers: (config: {
      queries: { groupId: number; periodId?: number; page?: number; perPage?: number };
    }) => req<QsSuppliersResponse>('get', '/external/kios/quick-search/suppliers')(config),
    QuickSearchController_getSupplier: (config: { params: { id: number } }) =>
      req<ReplaceGeneralDetails>('get', '/external/kios/quick-search/suppliers/:id')(config),
    QuickSearchController_getProvinces: () =>
      req<QsProvince[]>('get', '/external/kios/quick-search/provinces')(),

    // --- Supplier profiles (Kios external) ---
    SupplierProfileController_getA3Preview: (config: {
      params: { customerOperationCode: string };
    }) => req<string>('get', '/external/kios/:customerOperationCode/a3-preview')(config),
    SupplierProfileController_listSupplierProfiles: (config: {
      params: { customerOperationCode: string };
      queries?: {
        status?: string;
        classificationGroup?: string;
        pageIndex?: number;
        pageSize?: number;
        sorts?: string;
      };
    }) =>
      req<ReplaceSupplierResponse>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles'
      )(config),
    SupplierProfileController_getSupplierProfileDetail: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
      };
    }) =>
      req<SupplierProfileDetail>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode'
      )(config),
    SupplierProfileController_getBusinessRegistrationDetail: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
        businessRegistrationOperationCode: string;
      };
    }) =>
      req<ReplaceBusinessRegistrationCertificateDetail>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/business-registrations/:businessRegistrationOperationCode'
      )(config),
    SupplierProfileController_listCertificateBySupplierProfile: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
      };
      queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
    }) =>
      req<ReplaceFoodSafetyCertificate[]>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/certificates'
      )(config),
    SupplierProfileController_getCertificateDetail: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
        certificateOperationCode: string;
      };
    }) =>
      req<ReplaceFoodCertificateDetail>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/certificates/:certificateOperationCode'
      )(config),
    SupplierProfileController_listContractBySupplierProfile: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
      };
      queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
    }) =>
      req<ReplaceContractFromKios[]>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/contracts'
      )(config),
    SupplierProfileController_getContractDetail: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
        contractOperationCode: string;
      };
    }) =>
      req<ReplaceContractDetail>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/contracts/:contractOperationCode'
      )(config),
    SupplierProfileController_listDocumentBySupplierProfile: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
      };
      queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
    }) =>
      req<ReplaceOtherDocument[]>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/documents'
      )(config),
    SupplierProfileController_getDocumentDetail: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
        documentOperationCode: string;
      };
    }) =>
      req<ReplaceOtherDocumentDetails>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/documents/:documentOperationCode'
      )(config),
    SupplierProfileController_listBusinessRegistrationsBySupplierProfile: (config: {
      params: {
        customerOperationCode: string;
        supplierProfileOperationCode: string;
      };
      queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
    }) =>
      req<ReplaceBusinessRegistrationCertificate[]>(
        'get',
        '/external/kios/:customerOperationCode/supplier-profiles/:supplierProfileOperationCode/business-registrations'
      )(config),

    // --- Client resources (map dialogs) ---
    ClientAbridgedMenuController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) => req<GateResource[]>('get', '/resource/client/:orgId/type/abridged-menu/:mode')(config),
    ClientDailyConsolidatedInvoiceController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) =>
      req<GateResource[]>(
        'get',
        '/resource/client/:orgId/type/daily-consolidated-invoice/:mode'
      )(config),
    ClientDetailMenuBalanceController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) =>
      req<GateResource[]>('get', '/resource/client/:orgId/type/detail-menu-balance/:mode')(config),
    ClientNutritionMenuController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) => req<GateResource[]>('get', '/resource/client/:orgId/type/nutrition-menu/:mode')(config),
    ClientTeacherMenuController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) => req<GateResource[]>('get', '/resource/client/:orgId/type/teacher-menu/:mode')(config),
    ClientFoodTraceabilityController_list: (config: {
      params: { orgId: string; mode: string };
      queries?: SearchQueries;
    }) => req<GateResource[]>('get', '/resource/client/:orgId/type/food-traceability/:mode')(config),
    LogiController_listFoodReceivingRecords: (config?: {
      queries?: {
        site_id?: number;
        site_code?: string;
        date?: string;
        date_from?: string;
        date_to?: string;
        include?: string;
        limit?: number;
        offset?: number;
      };
    }) =>
      req<LogiReceivingRecord[]>('get', '/external/logi/food-receiving-records')(config ?? {}),

    LogiController_listSampleMeals: (config?: {
      queries?: {
        site_id?: number;
        site_code?: string;
        week_start?: string;
        date?: string;
        date_from?: string;
        date_to?: string;
      };
    }) => req<SampleMealsWeekResponse>('get', '/external/logi/sample-meals')(config ?? {}),

    // --- Dashboard (menu-dish statistics, B1) ---
    DashboardController_getMenuDishStatistics: (config: {
      queries: { customer_operation_code: string; month_key: string };
    }) => req<MenuDishStatisticsResponse>('get', '/dashboard/menu-dish-statistics')(config),

    // --- Dashboard (access statistics) ---
    DashboardController_getAccessStatistics: (config: {
      queries: { month_key: string };
    }) => req<AccessStatisticsResponse>('get', '/dashboard/access-statistics')(config),

    // --- Dashboard (thời gian trang hiển thị, để tính thời gian truy cập trung bình) ---
    // Khai ở đây để đủ bộ endpoint, nhưng KHÔNG phải đường gửi chính: useEngagementReporter tự
    // gọi fetch riêng (cần keepalive lúc rời trang, axios/XHR không hỗ trợ). Đừng tưởng nhầm là
    // code chết rồi xoá, và đừng đổi hook sang gọi qua method này — sẽ mất keepalive.
    DashboardController_postAccessEngagement: (config: { data: { engaged_ms: number } }) =>
      req<void>('post', '/dashboard/access-engagement')(config),

    // --- MenuPlan (khung thực đơn tháng theo năm học, qua BE proxy) ---
    MenuPlanController_getMonthlyMenuFrames: (config?: {
      queries?: { year?: number; semester?: 1 | 2; month?: string };
    }) =>
      req<MonthlyMenuFramesResponse>('get', '/external/menuplan/monthly-menu-frames')(config ?? {}),

    // --- KIOS: kỳ kinh doanh (năm học), qua BE proxy ---
    KiosController_getPlanningPeriods: (config?: object) =>
      req<PlanningPeriod[]>('get', '/external/kios/planning-periods')(config ?? {}),
  };

  return { ...api, axios: axiosInstance };
}

export function apiClient(): ApiClient & { axios: AxiosInstance } {
  if (!_instance) _instance = createApiClient();
  return _instance;
}

/** Client interface - add methods here as you migrate from clientTypes */
export interface ApiClient {
  /**
   * @returns Thông tin của người dùng đang đăng nhập
   */
  UserController_getData: () => Promise<UserDetail>;
  UserController_editData: (body: unknown) => Promise<UserDetail>;
  UserController_logout: (config?: unknown) => Promise<LogoutResponse>;
  UserController_changePassword: (body: {
    oldPassword: string;
    newPassword: string;
  }) => Promise<LogoutResponse>;
  /**
   * @returns Roles của user đang đăng nhập
   */
  UserController_getRoleAssignments: () => Promise<RoleAssignments>;
  AuthenController_loginWithPassword: (body: {
    handle: string;
    password: string;
  }) => Promise<LoginResponse>;
  AuthenController_verifyConfirmationCode: (body: {
    code: string;
  }) => Promise<VerifyConfirmationCodeResponse>;
  /** Cấp token khách (không cần tài khoản). Sau này Duende thay bằng client_credentials. */
  AuthenController_loginAsGuest: () => Promise<{ token: string }>;
  ConfirmationCodeController_list: (config?: {
    queries?: { targetOrgId?: string; status?: string };
  }) => Promise<{ data: ConfirmationCode[] }>;
  ConfirmationCodeController_create: (
    body: CreateConfirmationCodeBody
  ) => Promise<{ data: CreatedConfirmationCode }>;
  ConfirmationCodeController_newCode: () => Promise<{ data: { code: string } }>;
  ConfirmationCodeController_update: (config: {
    params: { id: string };
    body: UpdateConfirmationCodeBody;
  }) => Promise<{ data: ConfirmationCode }>;

  UserManagementController_createUser: (body: CreateUserPayload) => Promise<RegisterUserSuccess>;
  UserManagementController_listUser: (config?: ParamsConfig) => Promise<UserListingResult>;
  /**
   
   * @returns  Thông tin chi tiết của 1 tài khoản 
   */
  UserManagementController_getUserData: (config: {
    params: { userId: string };
  }) => Promise<UserDetail>;
  /**
   * Chỉnh sửa thông tin của 1 tài khoản.
   */
  UserManagementController_editUserData: (config: {
    params: { userId: string };
    body: unknown;
    queries?: { activate?: boolean };
  }) => Promise<UserDetail>;
  /**
   * Chỉnh sửa mật khẩu của 1 tài khoản
   */
  UserManagementController_changePassword: (config: {
    params: { userId: string };
    body: { newPassword: string };
  }) => Promise<LogoutResponse>;
  /**
   * Gán vai trò cho 1 tài khoản.
   */
  UserManagementController_editUserRoles: (config: {
    params: { userId: string };
    body: unknown;
  }) => Promise<LogoutResponse>;
  /**
   * @returns Thông tin các vai trò mà 1 người dùng được gán
   */
  UserManagementController_getUserRoles: (config: {
    params: { userId: string };
  }) => Promise<RoleAssignments>;
  UserManagementController_getUserId: (config: {
    params: { handle: string };
  }) => Promise<UserIdResponse>;
  /**
   * @returns Danh sách các role có thể gán cho người dùng, cùng với thông tin client/gov agency liên quan để hiển thị ở tab gán vai trò
   */
  UserManagementController_getRoleAssignmentConfig: () => Promise<RoleAssignmentConfig>;

  KiosController_listClients: (config?: {
    queries?: { type?: 1 | 2 };
  }) => Promise<ClientFromKios[]>;
  KiosController_getClientDetails: (config: {
    params: { code: string };
  }) => Promise<ClientFromKios>;
  KiosController_listGovAgencies: () => Promise<GovAgencyFromKios[]>;

  // Quick Search — tra cứu NCC toàn hệ thống (KPP → PLHH → NMH → NCC → chi tiết)
  QuickSearchController_getChannels: () => Promise<QsChannel[]>;
  QuickSearchController_getClassifications: (config: {
    queries: { channelId: number };
  }) => Promise<QsClassification[]>;
  QuickSearchController_getGroups: (config: {
    queries: { channelId: number; classificationId: number; periodId?: number };
  }) => Promise<QsGroup[]>;
  QuickSearchController_getSuppliers: (config: {
    queries: { groupId: number; periodId?: number; page?: number; perPage?: number };
  }) => Promise<QsSuppliersResponse>;
  QuickSearchController_getSupplier: (config: {
    params: { id: number };
  }) => Promise<ReplaceGeneralDetails>;
  QuickSearchController_getProvinces: () => Promise<QsProvince[]>;
  KiosController_getGovAgencyDetails: (config: {
    params: { code: string };
  }) => Promise<GovAgencyFromKios>;
  KiosController_getHvsInfo: (config?: unknown) => Promise<HvsInfoFromKios>;

  /**
   * @returns URL public của trang KIOS A3 Preview để client mở xem mẫu in màu
   */
  SupplierProfileController_getA3Preview: (config: {
    params: { customerOperationCode: string };
  }) => Promise<string>;
  SupplierProfileController_listSupplierProfiles: (config: {
    params: { customerOperationCode: string };
    queries?: {
      status?: string;
      classificationGroup?: string;
      pageIndex?: number;
      pageSize?: number;
      sorts?: string;
    };
  }) => Promise<ReplaceSupplierResponse>;
  SupplierProfileController_getSupplierProfileDetail: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
    };
  }) => Promise<SupplierProfileDetail>;
  SupplierProfileController_getBusinessRegistrationDetail: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
      businessRegistrationOperationCode: string;
    };
  }) => Promise<ReplaceBusinessRegistrationCertificateDetail>;
  SupplierProfileController_listCertificateBySupplierProfile: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
    };
    queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
  }) => Promise<ReplaceFoodSafetyCertificate[]>;
  SupplierProfileController_getCertificateDetail: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
      certificateOperationCode: string;
    };
  }) => Promise<ReplaceFoodCertificateDetail>;
  SupplierProfileController_listContractBySupplierProfile: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
    };
    queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
  }) => Promise<ReplaceContractFromKios[]>;
  SupplierProfileController_getContractDetail: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
      contractOperationCode: string;
    };
  }) => Promise<ReplaceContractDetail>;
  SupplierProfileController_listDocumentBySupplierProfile: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
    };
    queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
  }) => Promise<ReplaceOtherDocument[]>;
  SupplierProfileController_getDocumentDetail: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
      documentOperationCode: string;
    };
  }) => Promise<ReplaceOtherDocumentDetails>;
  SupplierProfileController_listBusinessRegistrationsBySupplierProfile: (config: {
    params: {
      customerOperationCode: string;
      supplierProfileOperationCode: string;
    };
    queries?: { pageIndex?: number; pageSize?: number; sorts?: string };
  }) => Promise<ReplaceBusinessRegistrationCertificate[]>;

  ClientAbridgedMenuController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  ClientDailyConsolidatedInvoiceController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  ClientDetailMenuBalanceController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  ClientNutritionMenuController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  ClientTeacherMenuController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  ClientFoodTraceabilityController_list: (config: {
    params: { orgId: string; mode: string };
    queries?: SearchQueries;
  }) => Promise<GateResource[]>;
  LogiController_listFoodReceivingRecords: (config?: {
    queries?: {
      site_id?: number;
      site_code?: string;
      date?: string;
      date_from?: string;
      date_to?: string;
      include?: string;
      limit?: number;
      offset?: number;
    };
  }) => Promise<LogiReceivingRecord[]>;

  LogiController_listSampleMeals: (config?: {
    queries?: {
      site_id?: number;
      site_code?: string;
      week_start?: string;
      date?: string;
      date_from?: string;
      date_to?: string;
    };
  }) => Promise<SampleMealsWeekResponse>;

  /**
   * @returns Số liệu thống kê món trên thực đơn cho (khách hàng, tháng) — dữ liệu MenuPlan đẩy sang (B1)
   */
  DashboardController_getMenuDishStatistics: (config: {
    queries: { customer_operation_code: string; month_key: string };
  }) => Promise<MenuDishStatisticsResponse>;

  /**
   * @returns Thống kê lượt truy cập hệ thống của tháng: đang online, tổng lượt, thời gian trung
   * bình, lượt theo 4 nhóm vai trò, và đủ mọi ngày trong tháng cho biểu đồ. Khách cũng gọi được.
   */
  DashboardController_getAccessStatistics: (config: {
    queries: { month_key: string };
  }) => Promise<AccessStatisticsResponse>;

  /** Báo thời gian trang thật sự hiển thị. Gọi lúc ẩn tab / rời trang, KHÔNG gọi định kỳ. */
  DashboardController_postAccessEngagement: (config: { data: { engaged_ms: number } }) => Promise<void>;

  /**
   * @returns Khung thực đơn tháng theo NĂM HỌC (qua BE proxy sang MenuPlan).
   * Không truyền tham số = năm học hiện tại, cả 2 kỳ. `year` là năm học, KHÔNG phải năm dương lịch.
   * Không dùng đồng thời `month` và `semester` (BE trả 400 MONTH_WITH_SEMESTER).
   */
  MenuPlanController_getMonthlyMenuFrames: (config?: {
    queries?: { year?: number; semester?: 1 | 2; month?: string };
  }) => Promise<MonthlyMenuFramesResponse>;

  /**
   * @returns Kỳ kinh doanh (năm học) từ KIOS, qua BE proxy. Kỳ có `isCurrent` là kỳ hiện hành.
   * KIOS lỗi thì BE trả mảng rỗng chứ không phải 5xx — chỗ gọi tự lo đường lùi.
   */
  KiosController_getPlanningPeriods: (config?: object) => Promise<PlanningPeriod[]>;
}
